import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CandidateCard = ({ candidate, onStatusChange, onViewProfile, onScheduleInterview, onSendMessage }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusOptions = [
    { value: 'new', label: 'New Application' },
    { value: 'screening', label: 'Under Screening' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'interview_scheduled', label: 'Interview Scheduled' },
    { value: 'interviewed', label: 'Interviewed' },
    { value: 'offered', label: 'Offer Extended' },
    { value: 'hired', label: 'Hired' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      screening: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      shortlisted: 'bg-purple-100 text-purple-800 border-purple-200',
      interview_scheduled: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      interviewed: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      offered: 'bg-green-100 text-green-800 border-green-200',
      hired: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name={index < rating ? 'Star' : 'Star'}
        size={16}
        color={index < rating ? '#F59E0B' : '#D1D5DB'}
        className={index < rating ? 'fill-amber-500' : ''}
      />
    ));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={candidate?.avatar}
            alt={candidate?.avatarAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate mb-1">
                {candidate?.name}
              </h3>
              <p className="text-sm text-muted-foreground truncate mb-1">
                {candidate?.position}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="MapPin" size={14} />
                <span>{candidate?.location}</span>
              </div>
            </div>

            <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidate?.status)}`}>
              {statusOptions?.find(opt => opt?.value === candidate?.status)?.label}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-3 text-sm">
            <div className="flex items-center gap-1">
              <Icon name="Briefcase" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{candidate?.experience}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="IndianRupee" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{candidate?.expectedSalary}</span>
            </div>
            <div className="flex items-center gap-1">
              {getRatingStars(candidate?.rating)}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1 text-xs">
              <Icon name="Target" size={14} className="text-primary" />
              <span className="text-foreground font-medium">{candidate?.skillMatch}% Match</span>
            </div>
            <span className="text-muted-foreground text-xs">•</span>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Calendar" size={14} />
              <span>Applied {candidate?.appliedDate}</span>
            </div>
          </div>

          {isExpanded && (
            <div className="mb-3 p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email</p>
                  <p className="text-sm text-foreground">{candidate?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone</p>
                  <p className="text-sm text-foreground">{candidate?.phone}</p>
                </div>
              </div>
              <div className="mb-3">
                <p className="text-xs text-muted-foreground mb-1">Key Skills</p>
                <div className="flex flex-wrap gap-2">
                  {candidate?.skills?.map((skill, index) => (
                    <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Summary</p>
                <p className="text-sm text-foreground">{candidate?.summary}</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>

            <Button
              variant="default"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewProfile(candidate?.id)}
            >
              View Profile
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => onScheduleInterview(candidate?.id)}
            >
              Schedule
            </Button>

            <Button
              variant="outline"
              size="sm"
              iconName="MessageSquare"
              iconPosition="left"
              onClick={() => onSendMessage(candidate?.id)}
            >
              Message
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-border">
        <Select
          placeholder="Change Status"
          options={statusOptions}
          value={candidate?.status}
          onChange={(value) => onStatusChange(candidate?.id, value)}
          className="w-full sm:w-64"
        />
      </div>
    </div>
  );
};

export default CandidateCard;